import React, { useState, useEffect } from "react";
import { Button, Card, Container, Spinner, Col, Navbar } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ImRadioChecked2 } from "react-icons/im";
import { FaCheckCircle } from "react-icons/fa";
import axios from "axios";

import {
  setShowErrorModal,
  setShowSuccessModal,
} from "../store/slices/preferenceSlice";
import { getUrl } from "../helpers/getUrl";

import { useCmsHook } from "../helpers/cmsHook";
import CmsEditAndSave from "../helpers/CmsEditAndSave";
import { CmsConstructor } from "../helpers/CmsConstructor";

const Retrigger = () => {
  const data = {
    triggered: {
      label: "Triggered",
      value: undefined,
    },
    findCreateCustomer: {
      label: "Find/Create Customer",
      value: undefined,
    },
    findCreateProducts: {
      label: "Find Products",
      value: undefined,
    },
    syncInvoice: {
      label: "Create invoice/order",
      value: undefined,
    },
  };
  const [stageData, setStageData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);
  const [retriggeredNote, setRetriggeredNote] = useState("");
  const [dealStage, setDealStage] = useState("");
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { cmsInstance, setCmsInstance, isAllowedUser } = useCmsHook();

  /**
   * @description This function is responsible for retriggering the flow
   */
  const onRetriggerFlow = async () => {
    try {
      setRetriggeredNote("");
      setIsLoading(true);
      const portalId = searchParams.get("portalId");
      const dealId = searchParams.get("dealId");
      const generate = searchParams.get("generate");

      await axios.post(getUrl("REACT_APP_RETRIGGER"), {
        portalId,
        dealId,
        generate,
      });

      for (let stage in stageData) {
        stageData[stage].value = true;
      }
      setStageData({ ...stageData });
      setDealStage("");

      setRetriggeredNote(
        "The Tripletex draft order has been generated successfully"
      );
      dispatch(
        setShowSuccessModal({
          message: "Retriggered Successfully",
        })
      );
    } catch (error) {
      console.log(error);
      let errorMessage;

      if (error?.response?.data?.details?.error?.message) {
        errorMessage = error?.response?.data?.details?.error?.message;
      } else if (error?.response?.data) {
        errorMessage =
          error?.response?.data?.error?.message ??
          error?.response?.data.message;
      } else {
        errorMessage = error?.message;
      }
      setDealStage(error.response.data.errorStage);
      setRetriggeredNote(
        `The workflow couldn't run because of an error: ${errorMessage}`
      );
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const stageName = searchParams.get("stageName");
    const errorLogs = searchParams.get("errorLogs");
    if (errorLogs) {
      setRetriggeredNote(
        `The workflow couldn't run because of an error: ${errorLogs}`
      );
    }
    setDealStage(stageName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dealStage) {
      for (let stage in stageData) {
        stageData[stage].value = undefined;
      }
      setStageData({ ...stageData });

      for (let stage in stageData) {
        if (stage === dealStage) {
          stageData[stage].value = false;
          break;
        } else {
          stageData[stage].value = true;
        }
      }
    }
    setStageData({ ...stageData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealStage]);

  useEffect(() => {
    const cmsInstance = async () => {
      const cmsObj = await CmsConstructor(dispatch);
      setCmsInstance(cmsObj);
    };
    cmsInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderIcons = (value) => {
    if (value === true) {
      return <FaCheckCircle size={25} className="me-3 logs-success-icon" />;
    } else if (value === false) {
      return <ImRadioChecked2 size={25} className="me-3 logs-failed-icon" />;
    } else {
      return <div className="logs-empty-icon me-3"></div>;
    }
  };

  return (
    <div>
      <Navbar
        fixed="top"
        className="navbar py-3 d-flex flex-row justify-content-center"
      >
        <div className="text-center">
          {cmsInstance && cmsInstance.showRichText("cmsContent.header")}
        </div>
      </Navbar>
      {!cmsInstance ? (
        <div className="d-flex flex-row justify-content-center align-items-center">
          <div className="d-flex flex-column justify-content-center align-items-center">
            <div className="d-flex flex-row justify-content-center align-items-center">
              <Spinner animation="border" variant="success" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className="d-flex justify-content-center"
            style={{ paddingTop: isAllowedUser && "120px" }}
          >
            <CmsEditAndSave />
          </div>
          <Container className="d-flex flex-row justify-content-center">
            <div
              style={{ marginTop: "100px" }}
              className="d-flex flex-column justify-content-center align-items-center w-100"
            >
              <Col md={8} className="mb-5 mt-3">
                {cmsInstance &&
                  cmsInstance.showRichText(
                    "cmsContent.retrigger.text",
                    "text-center"
                  )}
              </Col>
              <Col md={6} className="d-flex flex-row justify-content-center">
                <div className="w-100">
                  {retriggeredNote && (
                    <Card
                      className="e-conomic-card"
                      style={{ marginBottom: "27px", marginTop: "0px" }}
                    >
                      <Card.Body className="e-conomic-card-body">
                        <p className="my-1 pricing-page-para">
                          <b>Note: </b> <br />
                          {retriggeredNote}
                        </p>
                      </Card.Body>
                    </Card>
                  )}

                  <div className="d-flex flex-row justify-content-center w-100">
                    <div>
                      {stageData &&
                        Object.keys(stageData).length > 0 &&
                        Object.keys(stageData).map((stage) => (
                          <div key={stage}>
                            <div className="d-flex flex-row align-items-center">
                              {renderIcons(stageData[stage].value)}
                              <b>{stageData[stage].label}</b>
                            </div>
                            {stage !== "syncInvoice" && (
                              <div
                                style={{
                                  borderLeft: `${
                                    stageData[stage].value === true
                                      ? "4px solid #08a742"
                                      : "4px solid #d9d9d9"
                                  }`,
                                  height: "30px",
                                  marginLeft: "10px",
                                }}
                              ></div>
                            )}
                          </div>
                        ))}
                      <div className="d-flex flex-row justify-content-center">
                        <Button
                          className="logs-retrigger-button mt-5"
                          onClick={() => onRetriggerFlow()}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            <span>Replay</span>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </div>
          </Container>
        </>
      )}
    </div>
  );
};

export default Retrigger;
